<template>
  <f7-list-item
    :title="displayName"
    :subtitle="isManual ? '' : item.channelName"
    :text="item.notes"
    :swipeout="false"
    link="#"
    :class="{ 'item-card--oos-actions': hasOosActions }"
    @click="handleClick"
  >
    <template #media>
      <!-- Manual (off-catalog) lines have no catalog avatar — render the branded
           monogram default instead of a broken <img> with a null src. -->
      <manual-item-avatar
        v-if="isManual"
        :label="displayName"
        :size="64"
        :alt="$t('item.manual.avatarAlt')"
      ></manual-item-avatar>
      <img
        v-else
        :src="item.avatarUrl ?? item.productAvatarUrl"
        class="item-media"
        :alt="displayName"
      />
    </template>

    <template #after>
      <!-- Manual line: a small hint chip so the member knows the team added
           this item (not from the feed). Reuses the slot the variant chip uses
           for catalog lines — where variantName mirrors manualLabel and would
           only duplicate the title. -->
      <div v-if="isManual" class="item-after">
        <f7-chip
          :text="$t('item.manual.badge')"
          class="item-chip item-chip--manual"
        >
          <template #media>
            <f7-icon f7="sparkles" size="13"></f7-icon>
          </template>
        </f7-chip>
      </div>
      <div v-else-if="item.variantName" class="item-after">
        <f7-chip
          :text="item.variantName"
          class="item-chip"
          color="gray"
        ></f7-chip>
      </div>
    </template>

    <template #footer>
      <div class="item-footer">
        <!-- Unified member issue presentation (useItemIssuePresentation):
             • ROUTABLE (needs-decision, or being-handled with a typed issue) →
               the shared chip; tapping the ROW opens that issue's card
               (handleClick routes there). Display mode: a <span> (via TurnPill)
               so it stays valid inside the row anchor. It carries a chevron.
             • being-handled with NO issue behind it (flag off / legacy
               leader-direct) → the same calm pill WITHOUT the chevron: honest
               status, and the row still opens the ordinary details sheet. The
               chevron is the ONLY difference between these two «شغالين عليها»
               pills, and it has to be — F7's own row chevron comes from
               link="#", which every row sets, so it cannot distinguish them.
             • out-of-stock WITH issue history (`lastIssueId`, ASK 8) → the same
               shared chip in its own muted hue: it keeps announcing «ناقص
               استوك», and tapping the row opens the resolved issue read-only.
               That is the whole point of the field — the member most wants the
               explanation exactly when `activeIssue` has gone null.
             • out-of-stock with nothing behind it (leader-direct / flag off) →
               calm terminal pill, non-routing.
             • otherwise → the normal item status chip. An ordinary line whose
               issue was already sorted out keeps this branch on purpose; it
               offers its history from the details sheet instead of hijacking
               the row (see useItemIssuePresentation branch 4). -->
        <decision-chip
          v-if="isRoutable"
          :variant="chipVariant"
          class="item-decision-pill"
        ></decision-chip>
        <turn-pill
          v-else-if="issueKind === ISSUE_PRESENTATION.BEING_HANDLED"
          variant="handled"
          :announce="false"
          class="item-decision-pill"
        ></turn-pill>
        <!-- Inside the «نواقص» tab (`oosActions`) the out-of-stock verdict is
             not repeated on every row: the tab already says it. A routable
             DecisionChip above still renders, because it is also the cue that
             the row opens the issue's story. -->
        <turn-pill
          v-else-if="issueKind === ISSUE_PRESENTATION.OUT_OF_STOCK && !inOosTab"
          variant="oos"
          :announce="false"
          class="item-decision-pill"
        ></turn-pill>
        <f7-chip
          v-else-if="!inOosTab"
          :text="localizedStatus"
          :color="statusColor"
          class="item-chip item-chip--status"
        ></f7-chip>

        <!-- «اطلب الإلغاء» is waiting on the admin — quiet amber chip beside
             whichever status chip already renders above, never a button (the
             footer lives inside the row's own anchor). -->
        <f7-chip
          v-if="hasCancelRequest"
          :text="$t('item.status.badges.cancelRequested')"
          outline
          color="orange"
          class="item-chip item-chip--cancel-requested"
        >
          <template #media>
            <f7-icon f7="clock" size="12"></f7-icon>
          </template>
        </f7-chip>

        <!-- The supplier-availability hint for THIS line. Quiet inline text,
             not a chip, and visually weaker than every status above it — that
             ordering is the whole point. «نواقص» above is a VERDICT about her
             item; this is an observation about the product at the supplier, and
             the two must never be mistakable for each other.

             Suppressed entirely once the item carries its own issue or terminal
             status (see `showAvailabilityHint`): describing one event twice is
             exactly the duplication the backend avoids by excluding the affected
             member from the fan-out audience. -->
        <span v-if="showAvailabilityHint" class="item-availability-hint">
          {{ availabilityHintLabel }}
        </span>

        <!-- She told us where to look — and now, what the club said back.
             Quiet, single line, same weight as the availability hint above it:
             this is a receipt, not a status.

             IT USED TO VANISH ON AN ANSWER, because the backend dropped the key
             the moment the admin accepted or declined — so the last thing she
             ever read about her own tip was «اقتراحك وصل للأدمن», and the
             decline reason never reached her at all (gap U-40). The answer is
             stored now, and this line carries it for thirty days.

             Never a button here. The row is already tappable and the way to
             correct a suggestion lives in the details sheet; a second control
             inside an f7-list-item anchor would also be invalid markup. -->
        <span v-if="suggestionReceipt" class="item-suggestion-hint">
          {{ suggestionReceipt }}
        </span>

        <!-- The row says a door exists (U-32). An out-of-stock line used to
             carry the red chip and nothing else: the availability hint above is
             suppressed past PENDING, and «تعرف مين عنده الصنف؟» sat two taps
             away inside the details sheet with nothing pointing at it.

             A CUE, NOT A BUTTON. The row itself is already the tap target — and
             the whole footer renders inside the list item's anchor, so a second
             control here would be invalid markup AND would double-fire the row's
             own handler. Tapping goes where it already went: the question page
             for a line that had one (the door is there too now), the details
             sheet otherwise. Both ends carry the door.

             Mutually exclusive with the availability hint above by
             construction (that one requires PENDING). It can now sit BELOW an
             answered receipt, which is deliberate: the answer is what happened,
             the cue is what she can do next. -->
        <span v-if="showSuggestionCue && !inOosTab" class="item-suggestion-cue">
          {{ $t('item.suggestSupplier.rowCue') }}
        </span>

        <!-- A collector is out trying for it RIGHT NOW (`inActivePlan`). Only in
             the «نواقص» tab, where it explains why «إلغاء الصنف» is missing: the
             backend refuses a cancel while the line is on a live trip. -->
        <span v-if="showSearching" class="item-searching-hint">
          {{ $t('box.oos.searching') }}
        </span>

        <!-- Her return, on this line (returns-desk P3-A part B) — one of the
             six words, presence-driven, plus her reason as its own quiet chip.
             Same weight as the two hints above it: a fact, not a verdict. -->
        <div v-if="supplierReturn" class="item-return-status">
          <span class="item-return-hint">{{ returnStatusText }}</span>
          <f7-chip
            v-if="returnReasonText"
            :text="returnReasonText"
            outline
            class="item-chip item-chip--return-reason"
          ></f7-chip>
        </div>

        <div class="item-footer-info">
          <div class="info-item">
            <f7-icon f7="number" size="14" color="gray"></f7-icon>
            <span class="info-value">{{ item.qty }}</span>
          </div>

          <div class="info-divider" />

          <div class="info-price">
            {{ totalPrice }}
          </div>
        </div>
      </div>
    </template>

    <!-- THE ROW'S OWN DOORS, in the «نواقص» tab only (carton-oos P1-B). The
         `root` slot renders inside the <li> but OUTSIDE the row's link anchor
         (framework7-vue list-item.js), which is the only place a button can
         live on this row: inside the anchor it would be invalid markup and
         would fire the row's own tap as well.

         Both buttons hand the item to the page, which opens the SAME
         suggestion sheet and the SAME one-layer confirm the details sheet
         uses. Each is gated exactly as it is there. -->
    <template v-if="hasOosActions" #root>
      <div class="item-oos-actions">
        <button
          v-if="showChangeAction"
          type="button"
          class="item-oos-actions__change"
          @click="emit('suggest', item)"
        >
          {{ $t('item.suggestSupplier.pending.change') }}
        </button>
        <div v-if="showSuggestAction || showCancelAction" class="item-oos-actions__row">
          <f7-button
            v-if="showSuggestAction"
            outline
            class="item-oos-actions__btn item-oos-actions__suggest"
            @click="emit('suggest', item)"
          >
            <f7-icon f7="map_pin_ellipse" size="17"></f7-icon>
            {{ $t('box.oos.suggest') }}
          </f7-button>
          <f7-button
            v-if="showCancelAction"
            outline
            color="red"
            class="item-oos-actions__btn item-oos-actions__cancel"
            @click="emit('cancel', item)"
          >
            <f7-icon f7="xmark_circle" size="17"></f7-icon>
            {{ $t('item.status.actions.cancelOutOfStock') }}
          </f7-button>
        </div>
      </div>
    </template>
  </f7-list-item>
</template>

<script setup>
/**
 * ItemCard — a single-root f7-list-item row (audit B7). The details / edit /
 * issue sheets live in ItemSheets.vue as one page-level instance; tapping a
 * row emits `select` and OrdersPage opens the details sheet for it. The
 * single root also means attrs (style/data-index) fall through to the <li>,
 * which the virtualized list (P9) relies on.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DecisionChip from '@/components/collection/DecisionChip.vue';
import TurnPill from '@/components/collection/TurnPill.vue';
import ManualItemAvatar from '@/components/orders/ManualItemAvatar.vue';
import { useItemStatus } from '@/composables/commerce/useItemStatus.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { useIssueNavigation } from '@/composables/useIssueNavigation.js';
import {
  issueChipVariant,
  ISSUE_PRESENTATION,
  useItemIssuePresentation,
} from '@/composables/useItemIssuePresentation.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { hasPendingCancelRequest } from '@/models/commerce/OrderBoxItem.js';
import { isManualItem, itemDisplayName, lineNetCents } from '@/utils/orderBoxItemDisplay.js';
import { availabilityHint, formatAvailabilityDate } from '@/utils/productAvailability.js';
import {
  readSupplierReturn,
  returnReasonLabel,
  returnStatusLabel,
} from '@/utils/supplierReturn.js';
import {
  canSuggestSupplier,
  hasOpenSupplierSuggestion,
  readSupplierSuggestion,
  suggestionLabel,
} from '@/utils/supplierSuggestion.js';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  // The carton this line sits in, or null while it hasn't loaded — the whole
  // object rather than separate status/deliveredConfirmedAt props, since this
  // card carries no box-shaped prop today (oos-desk P3-E).
  box: {
    type: Object,
    default: null,
  },
  // The «نواقص» tab (carton-oos P1-B): an out-of-stock row carries its own
  // «أعرف مين عنده» / «إلغاء الصنف» buttons instead of the text cue, and drops
  // the verdict pill the tab already states. Off everywhere else.
  oosActions: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'suggest', 'cancel']);

const { t, locale } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();
const { openIssue } = useIssueNavigation();
// The carton is passed so `canCancel` can answer for an OUT_OF_STOCK line — it
// fails closed without one (see useItemStatus). Nothing else reads it.
const itemStatus = useItemStatus(() => props.item, () => props.box);

// Status display — all derived from useItemStatus, no manual mapping
const localizedStatus = itemStatus.label;
const statusColor     = itemStatus.color;

// Manual (off-catalog) line handling — see utils/orderBoxItemDisplay.js.
const isManual    = computed(() => isManualItem(props.item));
const displayName = computed(() => itemDisplayName(props.item));

// Net line total (post-discount) — for a Poola line this is the cash paid, not
// the full retail price. Identical to price*qty for normal lines (discount 0).
const totalPrice = computed(() => formatCurrency(lineNetCents(props.item)));

// Unified member issue presentation — the ONE classifier that decides whether
// this row shows an actionable decision chip, a calm "being handled" / "out of
// stock" pill, or the normal status chip (see useItemIssuePresentation).
const { presentation } = useItemIssuePresentation(() => props.item);
const issueKind = computed(() => presentation.value.kind);

// Can this row open an issue card, and if so whose court does the chip announce?
// ONE derivation answers both (null = no chip), so the two can never disagree —
// and the totals breakdown asks the same helpers, so the two surfaces can never
// disagree with each other either.
const chipVariant = computed(() => issueChipVariant(presentation.value));
const isRoutable = computed(() => chipVariant.value !== null);

// ── «اطلب الإلغاء» — her cancel request is waiting on the admin ──────────────
//
// BE→FE brief 2026-09-21-member-cancel-request. Presence-driven, like every
// other omitted-key fact this row reads: `cancelRequestedAt` is the only
// thing tested (see `hasPendingCancelRequest` in models/commerce/OrderBoxItem.js,
// the same reader the details sheet's waiting chip uses — one definition, so
// the row and the sheet cannot drift). Independent of `issueKind` above: this
// line has no issue, only a request the leader has not answered yet, so the
// chip sits BESIDE whatever status chip the branches above already render,
// not in place of it.
const hasCancelRequest = computed(() => hasPendingCancelRequest(props.item));

// ── Supplier-availability hint ───────────────────────────────────────────────
//
// The line's OWN mark, already narrowed backend-side to this item's variant plus
// any whole-product claim — a sibling variant being out never reaches here.
//
// Shown ONLY on a PENDING line, and only when the line has no issue presentation
// of its own. Two suppressions, for two different reasons:
//
//  1. PAST PENDING, it is no longer news. Once the club is collecting, has
//     collected, packaged or shipped the item, what a supplier's shelf looked
//     like is behind her — the item's own status is the live truth.
//  2. WITH AN ISSUE OF ITS OWN, it would describe one event twice. An item
//     carrying «نواقص» or a decision request already tells her about THIS item;
//     the availability hint is about the product in general, and stacking them
//     invites her to read one problem as two. This is the same duplication the
//     backend avoids by excluding the affected member from the fan-out audience.
const availability = computed(() => availabilityHint(props.item));

// ONE coercion for every strict status test on this row, READ OFF the composable
// this component already holds rather than re-derived. A `"1"` off the wire
// renders a correct chip (ItemStatusMeta is object-key access, which coerces)
// while answering false to every `===` beside it — so a genuinely PENDING line
// silently lost its availability hint. Re-typing the rule here would put two
// answers to "what status is this line" inside one component.
const coercedStatus = itemStatus.currentStatus;

const showAvailabilityHint = computed(
  () =>
    availability.value !== null &&
    coercedStatus.value === ItemStatus.PENDING &&
    issueKind.value === ISSUE_PRESENTATION.NONE,
);

const availabilityHintLabel = computed(() => {
  if (!availability.value) return '';
  const date = formatAvailabilityDate(availability.value.since, locale.value);

  return availability.value.supplier
    ? t('item.availability.hint.withSupplier', { date, supplier: availability.value.supplier })
    : t('item.availability.hint.noSupplier', { date });
});

// ── Her open supplier suggestion ────────────────────────────────────────────
//
// Shown on ANY status the backend still reports it on — which is the difference
// from the availability hint above. That hint is suppressed past PENDING and
// under an issue, because it describes the PRODUCT and would then be describing
// one event twice. This describes something SHE did, on this line, that nobody
// has answered yet; there is no second telling of it anywhere on the row, and it
// is most relevant precisely where the hint goes quiet — on an out-of-stock line.
//
// Absence is an omitted key, so the reader returning null is the whole gate.
const suggestion = computed(() => readSupplierSuggestion(props.item));

// The receipt reads identically on the row, in the details sheet and on a
// resolved question's card — one writer, so it cannot drift between them.
//
// IT NO LONGER DISAPPEARS ON AN ANSWER (gap U-40). The key used to be dropped
// the moment the admin accepted or declined, so the last thing she ever read
// about her own tip was «اقتراحك وصل للأدمن» followed by silence. The same line
// now carries the answer — including the decline REASON — for thirty days.
const suggestionReceipt = computed(() =>
  suggestionLabel(suggestion.value, { t, locale: locale.value, formatMoney: formatCurrency }),
);

// ── Her return, on THIS line (returns-desk P3-A part B) ─────────────────────
//
// Presence-driven, like every other omitted-key attribute on this row — the
// key is absent (no return, or a cancelled one) or it is not, and the reader
// never tests a specific value to decide whether to show anything.
const supplierReturn = computed(() => readSupplierReturn(props.item));
const returnStatusText = computed(() => returnStatusLabel(supplierReturn.value, { t }));
const returnReasonText = computed(() => returnReasonLabel(supplierReturn.value, { t }));

// The cue that says a door exists — the "before" half of the receipt above.
// Its status test is the OUT_OF_STOCK line only: PENDING is the availability
// hint's slot and belongs to it, and every other status is either past
// suggesting or has its own chip doing the talking.
//
// Coerced through the same `coercedStatus` every other strict test on this row
// reads, so a string `"41"` off the wire cannot answer false here while the chip
// beside it renders correctly.
const showSuggestionCue = computed(() => {
  // Only an OPEN tip closes the cue. Once the club has answered, she may well
  // know a second shop — and the answer sitting above this line is history, not
  // a reason to stop her.
  if (hasOpenSupplierSuggestion(props.item)) return false;
  if (coercedStatus.value !== ItemStatus.OUT_OF_STOCK) return false;
  // `?? undefined`: a host that has not (yet, or ever) wired up `box` gets the
  // three-arm, line-only answer — the same escape hatch `canSuggestSupplier`
  // keeps for a caller that omits the argument entirely (oos-desk P3-E). A host
  // that DOES pass the carton gets the fourth arm too: a moved-on box (packed,
  // shipped, delivered, closed, cancelled, or delivery-confirmed) hides the cue
  // even while the line itself still qualifies.
  return canSuggestSupplier(props.item, props.box ?? undefined);
});

// ── The «نواقص» tab's row buttons (carton-oos P1-B) ─────────────────────────
//
// Gated EXACTLY as the details sheet gates the same two doors (ItemSheets
// `canSuggest` / `canCorrectSuggestion` / `canCancelOutOfStock`), because a
// button that the backend then refuses is worse than no button:
//   - «أعرف مين عنده» while the line may take a tip and none is open;
//   - «غيّر اقتراحك» while a tip is open and the line still qualifies;
//   - «إلغاء الصنف» while `canCancel` holds — which is false on a live trip
//     (`inActivePlan`) and outside the carton's take-it-back window.
const inOosTab = computed(
  () => props.oosActions && coercedStatus.value === ItemStatus.OUT_OF_STOCK,
);
const lineMayTakeATip = computed(() => canSuggestSupplier(props.item, props.box ?? undefined));
const tipIsOpen = computed(() => hasOpenSupplierSuggestion(props.item));

const showSuggestAction = computed(() => inOosTab.value && lineMayTakeATip.value && !tipIsOpen.value);
const showChangeAction = computed(() => inOosTab.value && lineMayTakeATip.value && tipIsOpen.value);
// `?.`: a host that stubs the composable without `canCancel` simply gets no
// button, never a crash.
const showCancelAction = computed(() => inOosTab.value && itemStatus.canCancel?.value === true);

const hasOosActions = computed(
  () => showSuggestAction.value || showChangeAction.value || showCancelAction.value,
);

const showSearching = computed(() => inOosTab.value && props.item?.inActivePlan === true);

const handleClick = () => {
  // ANY item presenting an issue with a readable row behind it opens that issue —
  // awaiting the member (the answer form), being handled by the leader, or
  // out-of-stock (both read-only: their message plus the full timeline). All land
  // on the same /issues/:id/ card the notification and the inbox open. This is the
  // fix for "I can see something is wrong with this item but I can't find out what".
  //
  // Everything else — an issue with no typed row behind it, or a normal line
  // (including one whose issue is over) — opens the details sheet via `select`,
  // exactly as before.
  if (isRoutable.value) {
    openIssue(presentation.value.issueId);
    return;
  }
  emit('select', props.item);
};
</script>

<style scoped>
.item-media {
    width: 64px;
    height: 64px;
    border-radius: var(--poolit-radius-md);
    object-fit: cover;
}

.item-after {
    text-align: end;
}

.item-chip {
    font-size: var(--poolit-font-size-small);
}

/* Manual-line hint chip — soft brand tint, distinct from the gray variant chip. */
.item-chip--manual {
    --f7-chip-bg-color: var(--poolit-primary-50, #f5f0ff);
    color: var(--poolit-primary-700, #6621e2);
    font-weight: var(--poolit-font-weight-semibold, 600);
}

.dark .item-chip--manual {
    --f7-chip-bg-color: rgba(142, 91, 254, 0.18);
    color: var(--poolit-primary-200, #d7c2ff);
}

.item-chip--status {
    flex-shrink: 0;
}

/* «اطلب الإلغاء» waiting chip (BE→FE brief 2026-09-21-member-cancel-request) —
   quieter than the status chip beside it: a fact about what she asked, not a
   verdict about the line. Same small size as the return-reason chip below. */
.item-chip--cancel-requested {
    flex-shrink: 0;
    font-size: 10px;
}

.item-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
}

/* Weaker than every status chip beside it, on purpose (see the template note):
   plain text, no chip, tertiary colour, small. «نواقص» is a verdict about her
   item; this is an observation about the product, and the visual weight is what
   keeps the two from being mistaken for each other. */
.item-availability-hint {
    flex: 1 1 100%;
    min-width: 0;
    order: 1;
    font-size: 11px;
    line-height: 1.4;
    color: var(--poolit-text-tertiary, #8a938e);
}

/* Same visual weight as the availability hint above, and deliberately so: both
   are quiet facts that sit under the verdict chips rather than competing with
   them. `order: 2` keeps it below the hint when a line carries both. */
.item-suggestion-hint {
    flex: 1 1 100%;
    min-width: 0;
    order: 2;
    font-size: 11px;
    line-height: 1.4;
    color: var(--poolit-text-tertiary, #8a938e);
}

/* The "you can tell us where to find it" cue. Identical weight to the two hints
   above — it is an offer, not a status, and must never out-shout the نواقص chip
   it sits under. `order: 3` keeps the stack deterministic even though this one
   cannot co-occur with either of them. */
.item-suggestion-cue {
    flex: 1 1 100%;
    min-width: 0;
    order: 3;
    font-size: 11px;
    line-height: 1.4;
    color: var(--poolit-text-tertiary, #8a938e);
}

/* Her return's status — same visual register as the three quiet facts above
   it. `order: 4` keeps the stack deterministic; it cannot co-occur with the
   suggestion cue in practice (that one requires OUT_OF_STOCK, this one only
   ever shows on a DELIVERED line or one carrying a leader-opened return). */
.item-return-status {
    flex: 1 1 100%;
    min-width: 0;
    order: 4;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.item-return-hint {
    font-size: 11px;
    line-height: 1.4;
    color: var(--poolit-text-tertiary, #8a938e);
}

.item-chip--return-reason {
    font-size: 10px;
}

/* «بندوّر عليه في التجميع دلوقتي» — the same quiet register as the hints above,
   in the info blue: it is news that someone is out trying for it right now. */
.item-searching-hint {
    flex: 1 1 100%;
    min-width: 0;
    order: 3;
    font-size: 11px;
    line-height: 1.4;
    color: var(--f7-color-blue, #007aff);
}

/* The «نواقص» tab's row buttons. They sit below the row's link anchor inside
   the same <li>, so the row's divider moves from under the anchor to under the
   buttons — otherwise the hairline would cut the row in two and the buttons
   would read as belonging to the next item. */
.item-card--oos-actions :deep(.item-inner)::after {
    display: none;
}

.item-oos-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    padding: 0 var(--f7-list-item-padding-horizontal, 16px) 12px;
}

/* The same hairline F7 draws under .item-inner (device-pixel thin), so these
   rows' dividers match every other row's; F7's own last-row rule hides it on
   the last row the same way. */
.item-oos-actions::after {
    content: '';
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    height: 1px;
    background-color: var(--f7-list-item-border-color);
    transform: scaleY(calc(1 / var(--f7-device-pixel-ratio, 1)));
    transform-origin: 50% 100%;
}

.item-card--oos-actions:last-child .item-oos-actions::after {
    display: none;
}

.item-oos-actions__row {
    display: flex;
    gap: 8px;
}

.item-oos-actions__btn {
    --f7-button-height: 40px;
    --f7-button-font-size: 14px;
    --f7-button-border-radius: 12px;
    flex: 1 1 0;
    min-width: 0;
    gap: 6px;
}

/* An offer, in the brand tint — the same weight the details sheet gives the
   door; the cancel beside it is outline red. */
.item-oos-actions__suggest {
    --f7-button-text-color: var(--poolit-primary-700, #6621e2);
    --f7-button-outline-border-color: var(--poolit-primary-200, #d7c2ff);
    background-color: var(--poolit-primary-50, #f5f0ff);
}

.dark .item-oos-actions__suggest {
    --f7-button-text-color: var(--poolit-primary-200, #d7c2ff);
    --f7-button-outline-border-color: rgba(142, 91, 254, 0.45);
    background-color: rgba(142, 91, 254, 0.18);
}

.item-oos-actions__change {
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--poolit-primary-700, #6621e2);
    text-decoration: underline;
    cursor: pointer;
}

.dark .item-oos-actions__change {
    color: var(--poolit-primary-200, #d7c2ff);
}

.item-footer-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-inline-start: auto;
}

.info-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: var(--f7-text-color);
}

.info-value {
    font-weight: 500;
}

.info-divider {
    width: 1px;
    height: 16px;
    background-color: var(--f7-list-item-border-color);
}

.info-price {
    font-size: 15px;
    font-weight: 600;
    color: var(--f7-theme-color);
}
</style>
